import { ArrowLeftIcon, ArrowRightIcon } from "@chakra-ui/icons";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  ListItem,
  Select,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import FormInput from "../../../../../../components/input";
import StepHeader from "../../../../../../components/StepperHeader";
import { aimStatement } from "../forms";
import { useFormik } from "formik";
import { color } from "../../../../../../utils/theme";
import { Option } from "../../../../../../utils/types/props.dropdown";
import { useDispatch, useSelector } from "react-redux";
import {
  getFormData,
  saveFormData,
} from "../../../../../../redux/slice/formSlice";
import { RootState } from "../../../../../../redux/reducer/rootReducer";

interface AimStatementProps {
  onNext: () => void;
  onPrev: () => void;
  onSave: () => void;
}
const AimStatement = (props: AimStatementProps) => {
  const dispatch = useDispatch();
  const stepData = useSelector((state: RootState) => getFormData(state, "3"));

  const [showSummarizedAimStatement, setShowSummarizedAimStatement] =
    useState(false);

  const [showPopup, setShowPopup] = useState(false);
  const replacePlaceholders = () => {
    return `I/we, (qitName), aim to (aim) (problem) from (from) to (to)`
      .replace("(qitName)", `${formik.values.qitName} to ${formik.values.to}`)
      .replace("(aim)", formik.values.aim || "")
      .replace("(problem)", formik.values.aim || "")
      .replace("(from)", formik.values.from || "")
      .replace("(to)", formik.values.to || "");
  };

  const formik = useFormik({
    ...aimStatement,
    initialValues: stepData || aimStatement.initialValues,
    onSubmit: (values) => {
      console.log("Form values:", values);
      dispatch(saveFormData({ step: "3", data: values }));

      if (!saveAndExist) {
        setShowSummarizedAimStatement(false);
        props.onNext();
      } else {
        setTimeout(() => {
          if (props.onSave) props.onSave();
        }, 2000);
      }

      // }
    },
  });

  const aimOptions = [
    { value: "Improve", label: "Improve" },
    { value: "Reduce", label: "Reduce" },
    { value: "Initiate", label: "Initiate" },
    { value: "Redesign", label: "Redesign" },
  ];
  const [unitOfMeasureOptions] = useState<Option[]>([
    { value: "percentage", label: "Percentage" },
    { value: "ratio", label: "Ratio" },
    { value: "count", label: "Count" },
  ]);

  const cancelRef = React.useRef<HTMLButtonElement>(null);

  const [selectedAim, setselectedAim] = useState(stepData.aim || "");
  const [selectedBaselineUnitOfMeasure, setSelectedBaselineUnitOfMeasure] =
    useState(stepData.baselineMagnitudeUnitOfMeasure || "");
  const [selectedTargetUnitOfMeasure, setSelectedTargetUnitOfMeasure] =
    useState(stepData.targetMagnitudeUnitOfMeasure || "");

  const [saveAndExist, setSaveAndExist] = useState(false);

  const validateDuration = (from: string, to: string) => {
    if (!from || !to) return;

    const fromDate = new Date(from);
    const toDate = new Date(to);

    const durationInMonths =
      (toDate.getFullYear() - fromDate.getFullYear()) * 12 +
      (toDate.getMonth() - fromDate.getMonth());

    if (durationInMonths > 6) {
      setShowPopup(true); // Show popup if duration exceeds 6 months
    } else {
      setShowPopup(false); // Hide popup if duration is valid
    }
  };

  useEffect(() => {}, [formik.values]);

  return (
    <VStack h="100%" w="100%" spacing={4}>
      {showPopup && (
        <AlertDialog
          leastDestructiveRef={cancelRef}
          isOpen={showPopup}
          onClose={() => setShowPopup(false)}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Recommendation
              </AlertDialogHeader>
              <AlertDialogBody>
                The project duration exceeds 6 months. Please consider planning
                for a shorter period.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button onClick={() => setShowPopup(false)}>OK</Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}
      {/* Step Header */}
      <StepHeader
        title="Step 3: Aim Statement"
        description="Provide essential details about the project and team members."
        info={
          <Box
            textAlign={"left"}
            fontFamily={"sans-serif"}
            fontSize={14}
            fontWeight={"400"}
          >
            <Text color={color.primary}>
              <strong>Aim Statement should be SMART, meaning:</strong>
            </Text>
            <UnorderedList gap={2} mt={2}>
              <ListItem mt={2} flexDirection={"row"}>
                <strong color={color.primary}>Specific:</strong> Be specific
                about what you want to improve. Where and for who?
              </ListItem>
              <ListItem mt={2}>
                <strong>Measurable:</strong> Include a measurement that will
                evidence improvement. Create a baseline performance.
              </ListItem>
              <ListItem mt={2}>
                <strong>Achievable:</strong> Set a numerical target that can be
                realistically achieved.
              </ListItem>
              <ListItem mt={2}>
                <strong>Relevant:</strong> Your aim should align with facility
                goals and will improve patient care, experience, and outcomes.
              </ListItem>
              <ListItem mt={2}>
                <strong>Time-bound:</strong> Include a timeframe for your
                project, thinking about when you might see the outcomes.
              </ListItem>
            </UnorderedList>
          </Box>
        }
      />

      {/* Form Section */}
      <Box w="full" h={"full"} p={4} flex={1}>
        {showSummarizedAimStatement ? (
          <HStack
            spacing={4}
            p={4}
            display={"flex"}
            justifyContent={"center"}
            alignContent={"center"}
            alignItems={"flex-start"}
            height={"90%"}
            w={"100%"}
            justifyItems={"center"}
          >
            <Box
              w="60%"
              id="content"
              h={"100%"}
              alignSelf={"center"}
              justifySelf={"center"}
              alignContent={"center"}
              p={4}
              borderWidth="1px"
              borderRadius="md"
              bg="gray.50"
            >
              <Text
                fontWeight="bold"
                fontFamily={"sans-serif"}
                color={color.primary}
                fontSize={27}
                textDecoration={"underline"}
              >
                Generated Aim Statement
              </Text>
              <Text
                mt={2}
                fontSize={"x-large"}
                w={"70%"}
                mx={"auto"}
                alignSelf={"center"}
                fontWeight={"500"}
                fontFamily={"sans-serif"}
                color={color.text}
              >
                {replacePlaceholders() ||
                  "Click a template to preview it here!"}
              </Text>
            </Box>
          </HStack>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Flex direction="column" gap={6}>
              {/* Project Information */}
              <FormInput
                name="name"
                isRequired={true}
                value={formik.values.qitName}
                label="QIT Name"
                type="text"
                id="name"
                errorMessage={formik.errors.qitName as string}
                onChange={(event) => {
                  formik.setFieldValue("qitName", event.target.value);
                }}
              />
              <FormControl style={{ width: "100%" }}>
                <FormLabel
                  display={"flex"}
                  mb={0}
                  fontSize={18}
                  fontWeight={"500"}
                  color={color.text}
                >
                  Our aim is to
                  <Text color={"red"} mx={1} fontSize={24}>
                    *
                  </Text>
                </FormLabel>

                <Select
                  placeholder=" "
                  value={selectedAim}
                  onChange={(event) => {
                    setselectedAim(event.target.value);
                    formik.setFieldValue("aim", event.target.value);
                  }}
                >
                  {aimOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <Text
                  marginY={3}
                  fontWeight={"normal"}
                  color={"red"}
                  fontSize={13}
                  textAlign={"left"}
                >
                  {typeof formik.errors.aim === "string" && formik.errors.aim}
                </Text>
              </FormControl>
              {/* Dates */}
              <HStack spacing={4}>
                <FormInput
                  name="baselineMagnitude"
                  isRequired={true}
                  value={formik.values.baselineMagnitude}
                  label="Baseline Magnitude of the problem"
                  type="text"
                  id="baselineMagnitude"
                  errorMessage={formik.errors.baselineMagnitude as string}
                  onChange={(event) => {
                    formik.setFieldValue(
                      "baselineMagnitude",
                      event.target.value
                    );
                  }}
                  onBlur={formik.handleBlur}
                />
                <FormControl style={{ width: "100%" }}>
                  <FormLabel
                    display={"flex"}
                    mb={0}
                    fontSize={18}
                    fontWeight={"500"}
                    color={color.text}
                  >
                    Unit of measure
                    <Text color={"red"} mx={1} fontSize={24}>
                      *
                    </Text>
                  </FormLabel>

                  <Select
                    placeholder=" "
                    value={selectedBaselineUnitOfMeasure}
                    onChange={(event) => {
                      // setselectedAim(event.target.value);
                      setSelectedBaselineUnitOfMeasure(event.target.value);
                      formik.setFieldValue(
                        "baselineMagnitudeUnitOfMeasure",
                        event.target.value
                      );
                    }}
                  >
                    {unitOfMeasureOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  <Text
                    marginY={3}
                    fontWeight={"normal"}
                    color={"red"}
                    fontSize={13}
                    textAlign={"left"}
                  >
                    {typeof formik.errors.aim === "string" && formik.errors.aim}
                  </Text>
                </FormControl>
              </HStack>
              <HStack spacing={4}>
                <FormInput
                  name="targetMagnitude"
                  isRequired={true}
                  value={formik.values.targetMagnitude}
                  label="Target Magnitude of the problem"
                  type="text"
                  id="targetMagnitude"
                  errorMessage={formik.errors.targetMagnitude as string}
                  onChange={(event) => {
                    formik.setFieldValue("targetMagnitude", event.target.value);
                  }}
                  onBlur={formik.handleBlur}
                />
                <FormControl style={{ width: "100%" }}>
                  <FormLabel
                    display={"flex"}
                    mb={0}
                    fontSize={18}
                    fontWeight={"500"}
                    color={color.text}
                  >
                    Unit of measure
                    <Text color={"red"} mx={1} fontSize={24}>
                      *
                    </Text>
                  </FormLabel>

                  <Select
                    placeholder=" "
                    value={selectedTargetUnitOfMeasure}
                    onChange={(event) => {
                      // setselectedAim(event.target.value);
                      setSelectedTargetUnitOfMeasure(event.target.value);
                      formik.setFieldValue(
                        "targetMagnitudeUnitOfMeasure",
                        event.target.value
                      );
                    }}
                  >
                    {unitOfMeasureOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  <Text
                    marginY={3}
                    fontWeight={"normal"}
                    color={"red"}
                    fontSize={13}
                    textAlign={"left"}
                  >
                    {typeof formik.errors.aim === "string" && formik.errors.aim}
                  </Text>
                </FormControl>
              </HStack>

              <HStack
                spacing={4}
                display={"flex"}
                alignContent={"center"}
                alignItems={"center"}
                flexDirection={"row"}
                justifyContent={"center"}
              >
                <FormLabel
                  display={"flex"}
                  flex={0.3}
                  mt={5}
                  fontWeight={"500"}
                  color={"black"}
                >
                  Duration of the project
                  <Text color={"red"} mx={1} fontSize={24}>
                    *
                  </Text>
                </FormLabel>
                <HStack flex={0.7} spacing={4} display={"flex"}>
                  <FormInput
                    width={"30%"}
                    name="from"
                    isRequired={true}
                    value={formik.values.from}
                    label="From"
                    type="date"
                    id="from"
                    errorMessage={formik.errors.from as string}
                    onChange={(event) => {
                      formik.setFieldValue("from", event.target.value);
                      validateDuration(event.target.value, formik.values.to); // Validate on change
                    }}
                    onBlur={(event) => {
                      formik.handleBlur(event);
                      validateDuration(formik.values.from, formik.values.to); // Validate on blur
                    }}
                  />
                  <FormInput
                    width={"30%"}
                    name="to"
                    isRequired={true}
                    value={formik.values.to}
                    label="To"
                    type="date"
                    id="to"
                    errorMessage={formik.errors.to as string}
                    onChange={(event) => {
                      formik.setFieldValue("to", event.target.value);
                      validateDuration(formik.values.from, event.target.value); // Validate on change
                    }}
                    onBlur={(event) => {
                      formik.handleBlur(event);
                      validateDuration(formik.values.from, formik.values.to); // Validate on blur
                    }}
                  />
                </HStack>
              </HStack>
            </Flex>
          </form>
        )}
      </Box>
      <HStack
        flex={0.1}
        w="100%"
        justifyContent="space-between"
        p={4}
        bg="gray.50"
      >
        <Button
          colorScheme="blue"
          variant="outline"
          leftIcon={<ArrowLeftIcon />}
          onClick={() => {
            if (showSummarizedAimStatement) {
              formik.setFieldValue("aimStatement", "");
              setShowSummarizedAimStatement(false);
            } else {
              if (props.onPrev) {
                props.onPrev();
              }
            }
          }}
        >
          Previous
        </Button>
        <HStack>
          <Button
            colorScheme="blue"
            variant="outline"
            onClick={() => {
              setSaveAndExist(true);
              formik.handleSubmit();
            }}
          >
            Save & Exit
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={!formik.isValid}
            onClick={() => {
              console.log("Form values:", formik.values);
              console.log("Form errors:", formik.errors);
              console.log("formik.isValid", formik.isValid);
              setSaveAndExist(false);

              if (!showSummarizedAimStatement) {
                formik.setFieldValue("aimStatement", replacePlaceholders());
                setShowSummarizedAimStatement(true);
              }
              if (formik.isValid && formik.values.aimStatement === "") {
                formik.setFieldValue("aimStatement", replacePlaceholders());

                setShowSummarizedAimStatement(true);
              } else if (
                showSummarizedAimStatement &&
                formik.values.aimStatement !== ""
              ) {
                formik.handleSubmit();
              }
            }}
            w={"50%"}
            rightIcon={<ArrowRightIcon />}
          >
            Next
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );
};

export default AimStatement;
